import React, { useState, useEffect } from "react";
import axios from "axios";
import "./AllEvents.scss";
import { parseDate } from "../utils/date";

const AllEvents = () => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState('all');

    const monthNames = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    // Fetch all events from API
    useEffect(() => {
        const fetchEvents = async () => {
            try {
                setLoading(true);
                const response = await axios.get('/api/events/');
                const eventsData = Array.isArray(response.data) 
                    ? response.data 
                    : (response.data.results || response.data || []);
                
                setEvents(eventsData);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching events:', error);
                setLoading(false);
                setEvents([]);
            }
        };

        fetchEvents();
    }, []);

    const getEventTypeLabel = (type) => {
        const labels = {
            meeting: 'Monthly Meeting',
            sale: 'Fundraising Sale',
            cultural: 'Cultural Event',
            prayer: 'Prayer Service',
            planning: 'Planning Meeting',
            workshop: 'Workshop',
            fundraiser: 'Fundraiser',
            community: 'Community Service'
        };
        return labels[type] || type;
    };

    const formatEventDate = (dateString) => {
        try {
            if (!dateString) return { day: '', month: '', weekday: '' };
            const date = parseDate(dateString);
            if (isNaN(date.getTime())) {
                return { day: '', month: '', weekday: '' };
            }
            return {
                day: date.getDate(),
                month: monthNames[date.getMonth()].substring(0, 3),
                weekday: date.toLocaleDateString('en-US', { weekday: 'long' })
            };
        } catch (error) {
            console.error('Error formatting date:', dateString, error);
            return { day: '', month: '', weekday: '' };
        }
    };

    const cleanText = (text) => {
        if (!text) return '';
        return text
            .replace(/\\/g, '')
            .replace(/\n/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
    };

    const getFilteredEvents = () => {
        return events
            .map(event => ({
                ...event,
                date: event.date ? (event.date.split('T')[0] || event.date) : event.date,
                title: cleanText(event.title || 'Untitled Event'),
                time: event.time || 'All Day',
                location: cleanText(event.location || 'TBD'),
                description: cleanText(event.description || ''),
                type: event.type || 'meeting',
                id: event.id || event.title + event.date || Math.random().toString()
            }))
            .filter(event => event.date)
            .filter(event => selectedCategory === 'all' || event.type === selectedCategory)
            .sort((a, b) => new Date(a.date) - new Date(b.date));
    };

    const categories = [
        { key: 'all', label: 'All Events' },
        { key: 'meeting', label: 'Meetings' },
        { key: 'sale', label: 'Sales & Fundraising' },
        { key: 'cultural', label: 'Cultural Events' },
        { key: 'prayer', label: 'Prayer Services' },
        { key: 'planning', label: 'Planning & Committees' },
        { key: 'workshop', label: 'Workshops' },
        { key: 'fundraiser', label: 'Fundraisers' },
        { key: 'community', label: 'Community Service' }
    ];

    const filteredEvents = getFilteredEvents();

    return (
        <div className="all-events-page">
            <section className="events-hero">
                <div className="container">
                    <h1>All Events</h1>
                    <p>Browse through all our upcoming and past events</p>
                </div>
            </section>

            <section className="events-filters">
                <div className="container">
                    <div className="category-filters">
                        {categories.map(category => {
                            const eventCount = filteredEvents.filter(e => 
                                category.key === 'all' || e.type === category.key
                            ).length;
                            return (
                                <button
                                    key={category.key}
                                    className={`category-btn ${selectedCategory === category.key ? 'active' : ''}`}
                                    onClick={() => setSelectedCategory(category.key)}
                                >
                                    <span className="category-label">{category.label}</span>
                                    <span className="event-count">({eventCount})</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="events-list">
                <div className="container">
                    {loading ? (
                        <div className="loading-message">Loading events...</div>
                    ) : filteredEvents.length > 0 ? (
                        <div className="events-grid">
                            {filteredEvents.map((event, index) => {
                                const eventDate = formatEventDate(event.date);
                                return (
                                    <article 
                                        key={event.id} 
                                        className="event-card"
                                        style={{ animationDelay: `${index * 0.1}s` }}
                                    >
                                        <div className="event-card__header">
                                            <div className="event-date-badge">
                                                <span className="day">{eventDate.day}</span>
                                                <span className="month">{eventDate.month}</span>
                                            </div>
                                            <div className="event-type-badge">{getEventTypeLabel(event.type)}</div>
                                        </div>
                                        
                                        <div className="event-card__content">
                                            <div className="event-details">
                                                <div className="detail-item detail-item--time">
                                                    <svg className="detail-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <circle cx="12" cy="12" r="10"></circle>
                                                        <polyline points="12 6 12 12 16 14"></polyline>
                                                    </svg>
                                                    <span className="detail-text">{event.time || 'All Day'}</span>
                                                </div>
                                                <div className="detail-item detail-item--location">
                                                    <svg className="detail-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                                        <circle cx="12" cy="10" r="3"></circle>
                                                    </svg>
                                                    <span className="detail-text event-location" title={event.location || 'TBD'}>
                                                        {event.location || 'TBD'}
                                                    </span>
                                                </div>
                                            </div>
                                            
                                            <div className="event-weekday">{eventDate.weekday}</div>
                                            <h3 className="event-title">{event.title}</h3>
                                            
                                            {event.description && (
                                                <p className="event-description">{event.description}</p>
                                            )}
                                        </div>
                                        
                                        <div className="event-card__footer">
                                            <div className="event-card__accent"></div>
                                        </div>
                                    </article>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="no-events">
                            <h3>No events found</h3>
                            <p>Please check back soon or select a different category.</p>
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
};

export default AllEvents;

